import { debounce } from '../../../helpers/events';
import { isMobile } from '../../../helpers/device-detector';
import ParallaxBehaviour from '../../../helpers/parallax-behaviour';
import { StaticInterface } from '../../../helpers/static-interface';

export interface ITpxSignpostingUtils {
  applyMinimumHeight(): void;
}

export interface IStaticTpxSignpostingUtils {
  new(...args: any[]): ITpxSignpostingUtils;
  init(): void;
}

/**
 * Signposting utilities
 * @class
 */
export default class TpxSignpostingUtils implements StaticInterface<IStaticTpxSignpostingUtils, typeof TpxSignpostingUtils> {
  #container: HTMLElement;
  #element: HTMLElement;
  #image: HTMLElement | null;
  #youtubeContainer: HTMLElement | null;
  #youtubePlayBtn: HTMLElement | null;

  /**
   * @constructor
   * @param {HTMLElement} element The navigation bar root element
   */
  constructor(element: HTMLElement) {
    this.#element = element;
    this.#container = this.#element.querySelector('.o-signposting__container') || this.#element;
    this.#image = this.#element.querySelector('.o-signposting__image');
    this.#youtubeContainer = this.#element.querySelector('[data-component="YoutubeContainer"]');
    this.#youtubePlayBtn = this.#element.querySelector('[data-component="YoutubePlayButton"]');

    if (this.#image) {
      this.applyMinimumHeight();
      this.#addEventListeners();
    }

    // Apply parallax
    [...this.#element.querySelectorAll('[data-parallax]')].forEach(parallaxElement => {
      new ParallaxBehaviour(parallaxElement as HTMLElement, parseInt((parallaxElement as HTMLElement).dataset.parallax || '0'));
    });

    if (this.#youtubeContainer && this.#youtubePlayBtn) {
      this.#youtubePlayBtn.addEventListener("click", () => this.renderYoutubeVideo());
    }
  }

  #addEventListeners() {
    window.addEventListener('resize', debounce(() => this.applyMinimumHeight(), 100));
  }

  applyMinimumHeight() {
    if (!isMobile()) {
      this.#container.style.minHeight = `${this.#image!.clientHeight - 100}px`;
    }
    else {
      this.#container.style.removeProperty('minHeight');
    }
  }

  renderYoutubeVideo() {
    if (this.#youtubeContainer && this.#youtubeContainer.dataset.youtubeId) {
      this.#youtubeContainer.innerHTML = `<iframe class="o-signposting__youtube-video-iframe" src="https://www.youtube-nocookie.com/embed/${this.#youtubeContainer.dataset.youtubeId}?autoplay=1&mute=1&color=white&rel=0" allow="autoplay; encrypted-media" allowfullscreen></iframe>`;
    }
  }

  /**
   * Creates new instances of TpxSignpostingUtils for each `.o-signposting` element
   */
  static init() {
    const signpostings: Element[] = [...document.getElementsByClassName('o-signposting')];
    signpostings.forEach(signposting => {
      new TpxSignpostingUtils(signposting as HTMLElement);
    });
  }
}